import { styled } from '@mui/material/styles';
import LinkWithModal from '../../shared/LinkWithModal';
import { Theme } from '@mui/material/styles';
import { useRouter } from 'next/router';
import ButtonPrimary from '../../shared/Button/PrimaryButton';
import ButtonDiscreet from '../../shared/Button/ButtonDiscreet';
import { Grid } from '@mui/material';
import ButtonSecondary from '../../shared/Button/SecondaryButton';
import { scrollToElementById } from '../../../lib/utils/common';

export interface ModalCTAButton {
  name: string;
  fields: {
    buttonVariant: {
      value: string;
    };
    link: {
      value: {
        text: string;
        anchor: string;
      };
    };
  };
}

enum ActionType {
  SignUp = 'SignUp',
  Back = 'Back',
  Continue = 'Continue',
  Cancel = 'Cancel',
  Done = 'Done',
  Resend = 'Resend',
  SignOut = 'Sign out',
  Ok = 'Ok',
}

interface FooterModalProps {
  modalCTAButtons: ModalCTAButton[];
  onClose: () => void;
  isFormValid?: boolean;
  onCloseModals?: () => void;
  onResend?: (modal: string) => void;
  emailResendCount?: number;
  onSignOut?: () => void;
}

const getmModalCTAButtonByName = (buttons: ModalCTAButton[], name: string) => {
  const item = buttons.find((item) => item.name === name);
  if (item) {
    return {
      buttonVariant: item?.fields?.buttonVariant?.value,
      value: item?.fields?.link?.value?.text,
      anchor: item?.fields?.link,
    };
  } else {
    return null;
  }
};

export const replaceValue = (originaValue = '', lookupObj: Record<string, string>): string => {
  const tags = Object.keys(lookupObj);
  let replacedValue = originaValue;
  tags.forEach((tag) => {
    const tagValue = lookupObj[tag] || '';
    replacedValue = replacedValue.replace(new RegExp(`\\[${tag}\\]`, 'g'), tagValue as string);
  });
  return replacedValue;
};

const StFooterBox = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexGrow: '1',
  gap: '10px',
}));

export const StFooterbuttons = styled('div')(() => ({
  display: 'flex',
  marginTop: '25px',
  a: {
    padding: '12px 0px',
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '16px',
    '&:hover': {
      border: 'none',
      opacity: '0.9',
    },
  },
  '.signUpBtn': {
    '&:hover': {
      border: 'none',
      opacity: '0.9',
    },
  },
}));

export const AppBarLink = styled(LinkWithModal)(
  ({ theme, variant }: { variant?: string; theme?: Theme }) => ({
    textDecoration: 'none',
    fontFamily: `${theme?.palette?.primaryFontSemiBoldType || 'Proxima Nova SemiBold'}`,
    color: variant === 'contained' ? theme?.palette.white.main : theme?.palette.primary.main,
    '&:hover': {
      textDecoration: 'none',
    },
  })
);

export const ProfileFooterModal: React.FC<FooterModalProps> = ({
  modalCTAButtons,
  onClose,
  isFormValid,
}) => {
  const router = useRouter();
  const signUpBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.SignUp)?.value;
  const signUpBtnText = signUpBtn ? signUpBtn.replace(/([A-Z])/g, ' $1').trim() : '';
  const backBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Cancel);
  const continueBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Continue);

  const signUpBtnClick = async () => {
    onClose();

    const currentPath = window.location.pathname;

    if (currentPath === '/') {
      // Already on the home page, update hash and scroll
      history.pushState(null, '', '/#' + 'cr-registration');
      setTimeout(() => {
        scrollToElementById('cr-registration', { offset: 0 });
      }, 100);
    } else {
      await router.push('/', undefined, { scroll: false });
      setTimeout(() => {
        history.pushState(null, '', '/#' + 'cr-registration');
        scrollToElementById('cr-registration', { offset: 0 });
      }, 100);
    }
  };

  return (
    <Grid container sx={{ paddingTop: '20px' }}>
      <Grid item xs>
        <ButtonDiscreet id={signUpBtnText} onClick={signUpBtnClick}>
          {signUpBtnText}
        </ButtonDiscreet>
      </Grid>
      <Grid item xs="auto">
        <Grid container spacing={1}>
          <Grid item xs>
            <ButtonDiscreet id={backBtn?.value} onClick={onClose}>
              {backBtn?.value}
            </ButtonDiscreet>
          </Grid>
          <Grid item xs>
            <ButtonPrimary id={continueBtn?.value} type="submit" disabled={!isFormValid}>
              {continueBtn?.value}
            </ButtonPrimary>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export const SendFooterModal: React.FC<FooterModalProps> = ({
  modalCTAButtons,
  onClose,
  onCloseModals,
  onResend,
}) => {
  const backBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Back);
  const resendBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Resend);
  const doneBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Done);
  return (
    <StFooterbuttons>
      <ButtonSecondary onClick={onClose}>{backBtn?.value}</ButtonSecondary>
      <StFooterBox>
        <ButtonDiscreet onClick={() => onResend?.('emailSendModal')}>
          {resendBtn?.value}
        </ButtonDiscreet>
        <ButtonPrimary onClick={onCloseModals}>{doneBtn?.value}</ButtonPrimary>
      </StFooterBox>
    </StFooterbuttons>
  );
};

export const ReSendFooterModal: React.FC<FooterModalProps> = ({
  modalCTAButtons,
  onClose,
  onResend,
  onCloseModals,
  emailResendCount,
}: any) => {
  const backBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Back);
  const resendBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Resend);
  const doneBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Done);
  return (
    <StFooterbuttons>
      <ButtonSecondary onClick={onClose}>{backBtn?.value}</ButtonSecondary>
      <StFooterBox>
        {emailResendCount < 3 && (
          <ButtonDiscreet onClick={() => onResend?.('emailSendModal')}>
            {resendBtn?.value}
          </ButtonDiscreet>
        )}
        <ButtonPrimary onClick={onCloseModals}>{doneBtn?.value}</ButtonPrimary>
      </StFooterBox>
    </StFooterbuttons>
  );
};

export const SignOutFooterModal: React.FC<FooterModalProps> = ({
  modalCTAButtons,
  onClose,
  onSignOut,
}: any) => {
  const backBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Cancel);
  const signOutBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.SignOut);
  return (
    <StFooterbuttons>
      <StFooterBox>
        <ButtonDiscreet onClick={onClose}>{backBtn?.value}</ButtonDiscreet>
        <ButtonPrimary onClick={onSignOut}>{signOutBtn?.value}</ButtonPrimary>
      </StFooterBox>
    </StFooterbuttons>
  );
};

export const IdleSignOutFooterModal: React.FC<FooterModalProps> = ({
  modalCTAButtons,
  onClose,
}: any) => {
  const backBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Ok);
  return (
    <StFooterbuttons>
      <StFooterBox>
        <ButtonPrimary onClick={onClose}>{backBtn?.value || 'Ok'}</ButtonPrimary>
      </StFooterBox>
    </StFooterbuttons>
  );
};

export const PromptIdleTimeouFooterModal: React.FC<FooterModalProps> = ({
  modalCTAButtons,
  onClose,
  onSignOut,
}: any) => {
  const continueBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.Continue);
  const signOutBtn = getmModalCTAButtonByName(modalCTAButtons, ActionType.SignOut);
  return (
    <StFooterbuttons>
      <StFooterBox>
        <ButtonDiscreet onClick={onSignOut}>{signOutBtn?.value}</ButtonDiscreet>
        <ButtonPrimary onClick={onClose}>{continueBtn?.value}</ButtonPrimary>
      </StFooterBox>
    </StFooterbuttons>
  );
};
